import sampleInputs from 'data/legacyCheckoutPreviewSamples.json';
import blueIcon from 'assets/legacy-checkout-preview/blue-shipaid-icon.png';
import impactIcon from 'assets/legacy-checkout-preview/blue-impact-logo.svg';
import type { ResolvedLegacySampleV2 } from 'types/legacyCheckoutPreview';
import type {
  CheckoutPlusLegacySnapshot,
} from 'ui.shipaid.com/renderers/checkout-plus-legacy';

interface Presentation {
  readonly isGreen: boolean;
  readonly isFreeGiftEnabled: boolean;
  readonly detailsOpen: boolean;
  readonly hasProtectionInCart: boolean;
  readonly toggleLoading: boolean;
  readonly benefitsId: string;
  readonly controlLayout?: CheckoutPlusLegacySnapshot['controlLayout'];
}

export type LegacySampleResult =
  | {
      readonly status: 'available';
      readonly sample: ResolvedLegacySampleV2;
      readonly snapshot: CheckoutPlusLegacySnapshot;
    }
  | { readonly status: 'unavailable'; readonly message: string };

const freeze = (value: unknown): void => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
};
sampleInputs.forEach(freeze);
const staticArtwork = Object.freeze({ blue: blueIcon, impact: impactIcon });

// Admit only the sealed synthetic samples, including their exact copy/assets.
// This is not a pricing formula, live quote or customization validator.
const matches = (value: unknown, reference: unknown): boolean => {
  if (value === reference) return true;
  if (!value || !reference || typeof value !== 'object' || typeof reference !== 'object') return false;
  if (Array.isArray(value) !== Array.isArray(reference)) return false;
  const keys = Object.keys(reference);
  return Object.keys(value).length === keys.length && keys.every(key =>
    Object.prototype.hasOwnProperty.call(value, key) &&
    matches((value as Record<string, unknown>)[key], (reference as Record<string, unknown>)[key]),
  );
};

export function resolveLegacySample(input: unknown, presentation: Presentation): LegacySampleResult {
  const unavailable: LegacySampleResult = {
    status: 'unavailable', message: 'Sample preview unavailable. Try again.',
  };
  try {
    const sample = sampleInputs.find(reference => matches(input, reference)) as ResolvedLegacySampleV2 | undefined;
    if (!sample || !presentation ||
      !['isGreen', 'isFreeGiftEnabled', 'detailsOpen', 'hasProtectionInCart', 'toggleLoading']
        .every(key => typeof presentation[key as keyof Presentation] === 'boolean') ||
      typeof presentation.benefitsId !== 'string' ||
      !/^[a-zA-Z][a-zA-Z0-9_-]{0,79}$/.test(presentation.benefitsId) ||
      (presentation.controlLayout !== undefined && !['legacy', 'two-buttons', 'button-plus-link'].includes(presentation.controlLayout))) return unavailable;

    const { isGreen, isFreeGiftEnabled } = presentation;
    const copy = Object.freeze({ ...sample.copy,
      freeGiftSuffix: isFreeGiftEnabled ? sample.copy.freeGiftSuffix : '',
      withFreeGiftSuffix: isFreeGiftEnabled ? sample.copy.withFreeGiftSuffix : '',
      noGiftSuffix: isFreeGiftEnabled ? sample.copy.noGiftSuffix : '',
      treeSuffix: isGreen ? sample.copy.treeSuffix : '',
    });
    const snapshot: CheckoutPlusLegacySnapshot = Object.freeze({
      contractVersion: 1, isGreen, isFreeGiftEnabled,
      ...(presentation.controlLayout === undefined ? {} : { controlLayout: presentation.controlLayout }),
      detailsOpen: presentation.detailsOpen,
      hasProtectionInCart: presentation.hasProtectionInCart,
      toggleLoading: presentation.toggleLoading,
      benefitsId: presentation.benefitsId,
      iconImg: staticArtwork[sample.assets[isGreen || isFreeGiftEnabled ? 'impact' : 'blue'].assetId],
      price: new Intl.NumberFormat(sample.locale, { style: 'currency', currency: sample.currency })
        .format(Number(sample.selectedCatalogPrice)),
      checkoutTotal: null, checkoutWithoutProtectionTotal: null,
      customCss: '', copy,
    });
    return { status: 'available', sample, snapshot };
  } catch {
    return unavailable;
  }
}
